import { AnchorNodeData, AnchorTypeNodeData, NodePath } from '../tree.model';
import { BaseAnchor, BaseVersion } from 'src/models/anchor-and-version/base.model';
import { NodeType } from '../enums/node-type.enum';
import { TreeNode } from 'primeng/api';
import { TreeType } from '../enums/tree-type.enum';
import { createAnchorNodes } from './anchor-node.util';

const TREE_TYPES_WITH_ACTIVE_STATE = new Set<TreeType>([
    TreeType.AllocationElement,
    TreeType.Component,
    TreeType.DirectWirePortData,
    TreeType.InternalPortData,
    TreeType.Message,
    TreeType.Scenario,
    TreeType.Signal,
    TreeType.UseCase,
    TreeType.UserFunction,
]);

export function highlightAnchorNode(anchorNode: TreeNode<AnchorNodeData>): void {
    anchorNode.styleClass =
        (anchorNode.styleClass || '').replace('current-anchor', '').trim() + ' current-anchor';
}

export function removeHighlightFromAnchor(anchorNode: TreeNode<AnchorNodeData>): void {
    anchorNode.styleClass =
        (anchorNode.styleClass || '').replace('current-anchor', '').trim() || undefined;
}

export function updateNode(
    nodes: TreeNode[] | undefined,
    node: TreeNode,
    onRootUpdate: (nodes: TreeNode[]) => void
): boolean {
    if (!nodes) return false;
    for (let i = 0; i < nodes.length; i++) {
        if (nodes[i] === node || updateNode(nodes[i].children!, node, onRootUpdate)) {
            nodes[i] = { ...nodes[i] };
            if (!nodes[i].parent) onRootUpdate(nodes);
            return true;
        }
    }
    return false;
}

export function getNodeKey(nodePath: NodePath[]): string {
    return nodePath.map((p) => `${p.anchorType}-${p.key || 'root'}`).join('/');
}

export function getNodePath(node?: TreeNode): NodePath[] {
    const res: NodePath[] = [];
    let currentNode = node;
    while (currentNode) {
        if (currentNode.type === NodeType.anchor) {
            const data = currentNode.data as AnchorNodeData;
            res.unshift({ anchorType: data.anchorType, key: data.anchor.key });
        } else if (currentNode.type === NodeType.anchorType && currentNode.data?.anchorType) {
            res.unshift({ anchorType: currentNode.data.anchorType });
        }
        currentNode = currentNode.parent;
    }
    if (res.length === 0 && node?.type === NodeType.anchorType && node.data?.anchorType) {
        res.push({ anchorType: node.data.anchorType });
    }
    return res;
}

export function findNodeByKey(
    nodes: TreeNode[],
    key: number
): TreeNode<AnchorNodeData> | undefined {
    for (const node of nodes) {
        if ((node.data as AnchorNodeData)?.anchor?.key === key)
            return node as TreeNode<AnchorNodeData>;
        if (node.children) {
            const found = findNodeByKey(node.children, key);
            if (found) return found;
        }
    }
    return undefined;
}

export function createLoadMoreNode(parentNode: TreeNode): TreeNode {
    return {
        label: 'Load more...',
        type: NodeType.loadMore,
        leaf: true,
        selectable: false,
        styleClass: 'cursor-pointer',
        parent: parentNode,
    };
}

export function addNewAnchorsFirst(
    anchors: BaseAnchor<BaseVersion>[],
    newAnchors: BaseAnchor<BaseVersion>[],
    newlyCreatedAnchor?: BaseAnchor<BaseVersion>
): BaseAnchor<BaseVersion>[] {
    if (!newlyCreatedAnchor && newAnchors.length === 0) return anchors;

    const result: BaseAnchor<BaseVersion>[] = [];
    const seenAnchorKeys = new Set<number>();
    const addAnchor = (anchor: BaseAnchor<BaseVersion>): void => {
        if (seenAnchorKeys.has(anchor.key)) return;
        seenAnchorKeys.add(anchor.key);
        result.push(anchor);
    };

    if (newlyCreatedAnchor) {
        addAnchor(newlyCreatedAnchor);
    }
    newAnchors.forEach(addAnchor);
    anchors.forEach(addAnchor);

    return result;
}

export function buildAnchorNodesList(
    anchors: BaseAnchor<BaseVersion>[],
    anchorTypeNode: TreeNode<AnchorTypeNodeData>,
    hasMore: boolean,
    currentAnchorKey?: number
): TreeNode[] {
    if (anchors.length === 0) {
        return [
            {
                label: 'No results',
                type: NodeType.noResults,
                leaf: true,
                selectable: false,
            },
        ];
    }

    const anchorNodes = renderAnchorNodes(anchors, anchorTypeNode, currentAnchorKey);
    if (hasMore) {
        anchorNodes.push(createLoadMoreNode(anchorTypeNode));
    }
    return anchorNodes;
}

export function renderAnchorNodes(
    anchors: BaseAnchor<BaseVersion>[],
    anchorTypeNode: TreeNode<AnchorTypeNodeData>,
    currentAnchorKey?: number
): TreeNode[] {
    const anchorType = anchorTypeNode.data?.anchorType;
    const shouldMarkInactive = anchorType && TREE_TYPES_WITH_ACTIVE_STATE.has(anchorType);
    const shouldMarkNew = anchorType !== TreeType.ChangeRequest;
    const newAnchors: BaseAnchor<BaseVersion>[] = [];
    const otherAnchors: BaseAnchor<BaseVersion>[] = [];

    for (const anchor of anchors) {
        if (shouldMarkNew && isNewlyCreatedAnchor(anchor)) {
            newAnchors.push(anchor);
        } else {
            otherAnchors.push(anchor);
        }
    }

    const anchorNodes = createAnchorNodes([...newAnchors, ...otherAnchors], anchorTypeNode);
    anchorNodes.forEach((node) => {
        if (node.type === NodeType.anchor) {
            const anchorData = node.data as AnchorNodeData;
            let styleClass = '';

            if (shouldMarkNew && isNewlyCreatedAnchor(anchorData.anchor)) {
                styleClass += 'newly-created-anchor';
            }

            if (shouldMarkInactive && anchorData.anchor.validVersion?.isActive === false) {
                styleClass += (styleClass ? ' ' : '') + 'inactive-anchor';
            }

            if (currentAnchorKey && anchorData.anchor.key === currentAnchorKey) {
                styleClass += (styleClass ? ' ' : '') + 'current-anchor';
            }

            node.styleClass = styleClass || undefined;
        }
    });

    return anchorNodes;
}

export function isNewlyCreatedAnchor(anchor: BaseAnchor<BaseVersion>): boolean {
    return (
        !anchor.committedBy &&
        !anchor.committedTimestamp &&
        !anchor.deletedTimestamp &&
        !anchor.deletedInWorkspaceKey
    );
}
